import { FC, MouseEvent, ReactNode } from 'react'
import { Divider } from '@/components/divider/Divider'
import { Text } from '@/components/text/Text'
import { composeClasses } from '@/utils/compose-classes'
import { useModalContext } from '../../hooks'
import { MdOutlineChevronLeft, MdOutlineClose } from 'react-icons/md'

export interface ModalHeaderProps {
  title?: ReactNode
  subTitle?: ReactNode
  className?: string
  /**
   * @default true
   */
  isShowCloseButton?: boolean
  children?: ReactNode
  /**
   * @default true
   */
  isShowDivider?: boolean
  /**
   * @default false
   */
  isShowLeftChevron?: boolean
  onClickLeftChevron?: (event: MouseEvent) => void
}

export const _ModalHeader: FC<ModalHeaderProps> = ({
  title,
  className,
  subTitle,
  isShowCloseButton = true,
  isShowDivider = true,
  isShowLeftChevron = false,
  onClickLeftChevron,
  children
}) => {
  const { onClose } = useModalContext()

  if (children) {
    return <header className={className}>{children}</header>
  }

  if (!title) {
    throw Error('Modal.Header requires title when children are not provided.')
  }

  return (
    <>
      <header
        data-testid="modal-header"
        className={composeClasses(
          className,
          'w-full flex items-center justify-between flex-row px-4 pb-1 pt-3'
        )}
      >
        {isShowLeftChevron && (
          <button type="button" onClick={onClickLeftChevron ?? onClose}>
            <MdOutlineChevronLeft className="text-primary min-w-[24px] min-h-[24px]" />
          </button>
        )}
        <div className="flex flex-col items-start justify-center gap-1">
          <Text className="text-body-bold-l text-primary">{title}</Text>
          <Text className="text-body-bold-s text-secondary">{subTitle}</Text>
        </div>
        {isShowCloseButton && (
          <button
            onClick={onClose}
            data-testid="modal-close-button"
            type="button"
            className="hover:brightness-150"
          >
            <MdOutlineClose className="text-primary min-w-[24px] min-h-[24px]" />
          </button>
        )}
      </header>
      {isShowDivider && <Divider />}
    </>
  )
}
